import { 
  collection, 
  query, 
  where, 
  getDocs, 
  addDoc, 
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  orderBy,
  limit,
  startAfter
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { Rating } from '../models/Rating';

export class RatingService {
  static async getProductRatings(productId, pageSize = 10, lastDoc = null) {
    try {
      const ratingsRef = collection(db, 'ratings');
      let queryConstraints = [
        where('productId', '==', productId),
        orderBy('createdAt', 'desc'),
        limit(pageSize)
      ];

      if (lastDoc) {
        queryConstraints.push(startAfter(lastDoc));
      }

      const q = query(ratingsRef, ...queryConstraints);
      const snapshot = await getDocs(q);

      const ratings = snapshot.docs.map(doc => Rating.fromFirestore(doc));
      const lastVisible = snapshot.docs[snapshot.docs.length - 1] || null;

      return { ratings, lastVisible };
    } catch (error) {
      console.error('Error getting product ratings:', error);
      throw new Error('Failed to get product ratings');
    }
  }

  static async addRating(ratingData) {
    try {
      const ratingsRef = collection(db, 'ratings');
      const rating = new Rating({
        ...ratingData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      const docRef = await addDoc(ratingsRef, rating.toFirestore());
      return { id: docRef.id, ...rating };
    } catch (error) {
      console.error('Error adding rating:', error);
      throw new Error('Failed to add rating');
    }
  }

  static async updateRating(ratingId, ratingData) {
    try {
      const ratingRef = doc(db, 'ratings', ratingId);
      await updateDoc(ratingRef, {
        ...ratingData,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating rating:', error);
      throw new Error('Failed to update rating');
    }
  }

  static async deleteRating(ratingId) {
    try {
      const ratingRef = doc(db, 'ratings', ratingId);
      await deleteDoc(ratingRef);
    } catch (error) {
      console.error('Error deleting rating:', error);
      throw new Error('Failed to delete rating');
    }
  }

  static async getUserRating(productId, userId) {
    try {
      if (!productId || !userId) {
        console.error('Missing required parameters:', { productId, userId });
        return null;
      }

      const ratingsRef = collection(db, 'ratings');
      const q = query(
        ratingsRef,
        where('productId', '==', productId),
        where('userId', '==', userId)
      );
      const snapshot = await getDocs(q);
      
      if (snapshot.empty) {
        return null;
      }
      
      return Rating.fromFirestore(snapshot.docs[0]);
    } catch (error) {
      console.error('Error getting user rating:', error);
      throw new Error('Failed to get user rating');
    }
  }

  static async getProductAverageRating(productId) {
    try {
      const ratingsRef = collection(db, 'ratings');
      const q = query(ratingsRef, where('productId', '==', productId));
      const snapshot = await getDocs(q);
      
      if (snapshot.empty) {
        return 0;
      }
      
      const ratings = snapshot.docs.map(doc => doc.data().rating);
      const average = ratings.reduce((a, b) => a + b, 0) / ratings.length;
      return Number(average.toFixed(1));
    } catch (error) {
      console.error('Error calculating average rating:', error);
      throw new Error('Failed to calculate average rating');
    }
  }
} 